import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { DialogActions } from '@mui/material'
import { useQueryClient } from 'react-query'
import React from 'react'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import CustomDialog from 'shared/components/CustomDialog'

import ChangePasswordModal from './components/ChangePasswordModal'
import CreateUserModal from './components/CreateUserModal'
import { removeUserService } from '../../services'
import EditModal from './components/EditModal'
import { ModalInfoType } from '../../types'

interface Props {
   handleClose: () => void
   handleRefetch: () => void
   modalInfo?: ModalInfoType
}

export default function Modal({ handleClose, handleRefetch, modalInfo }: Props) {
   const { t } = useTranslation(['USERS_AND_SUBSCRIPTIONS', 'TRANSLATION'])
   const { addNotification } = useNotificationContext()
   const queryClient = useQueryClient()

   function removeUser() {
      if (modalInfo) {
         removeUserService(modalInfo.userInfo.uuid)
            .then((res) => {
               if (res.status === 200) {
                  addNotification('successSave', 'success')
                  queryClient.invalidateQueries('locations')
                  handleRefetch()
                  handleClose()
               }
            })
            .catch((error) => {
               if (isAxiosError<ErrorType>(error)) {
                  addNotification(error.response?.data?.errors[0].code || 'failedSave', 'error')
               } else {
                  addNotification('failedSave', 'error')
               }
            })
      }
   }

   function renderContent() {
      switch (modalInfo?.modalType) {
         case 'DELETE':
            return (
               <>
                  <DialogContent>
                     <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {t('USERS_AND_SUBSCRIPTIONS:deleteSureText', {
                           userName: `${modalInfo.userInfo.name} ${modalInfo?.userInfo.surname}`,
                        })}
                     </Typography>
                  </DialogContent>
                  <DialogActions>
                     <OutlinedButton isDelete variant="outlined" onClick={handleClose}>
                        {t('TRANSLATION:no')}
                     </OutlinedButton>
                     <ContainedButton isDelete variant="contained" onClick={removeUser}>
                        {t('TRANSLATION:yes')}
                     </ContainedButton>
                  </DialogActions>
               </>
            )
         case 'CHANGEPASSWORD':
            return <ChangePasswordModal handleClose={handleClose} uuid={modalInfo?.userInfo.uuid} />
         case 'EDIT':
            return (
               <EditModal
                  locationUuids={modalInfo.userInfo.locationUuids}
                  modalInfo={{ ...modalInfo.userInfo }}
                  handleClose={handleClose}
                  handleRefetch={handleRefetch}
               />
            )
         case 'CREATEUSER':
            return <CreateUserModal handleRefetch={handleRefetch} handleClose={handleClose} />
         default:
            return null
      }
   }
   return (
      <CustomDialog
         isLoading={!modalInfo}
         open={!!modalInfo?.modalType}
         deleteVariant={modalInfo?.modalType === 'DELETE'}
         onClose={handleClose}
         content={
            <>
               <DialogTitle>{t(`${modalInfo?.modalType?.toLowerCase()}`)}</DialogTitle>
               {renderContent()}
            </>
         }
      />
   )
}
